import React from 'react';
import { Building2, Target, Users, ArrowRight, CheckCircle, Globe2, Award, Briefcase, Heart, Shield, HandshakeIcon, Users2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function About() {
  const values = [
    { 
      icon: Heart, 
      title: "Passion", 
      description: "Driven by our love for technology and service excellence",
      details: "We approach every project with enthusiasm and dedication, constantly pushing the boundaries of what's possible in IT solutions. Our team's passion for technology translates into innovative solutions that exceed expectations."
    },
    { 
      icon: Shield, 
      title: "Integrity", 
      description: "Maintaining the highest ethical standards in all our dealings",
      details: "Trust is the foundation of our business. We operate with complete transparency, honesty, and accountability, ensuring that every decision and action aligns with our clients' best interests."
    },
    { 
      icon: HandshakeIcon, 
      title: "Commitment", 
      description: "Dedicated to delivering the best solutions for our clients",
      details: "We go above and beyond to ensure our clients' success. Our commitment extends beyond project completion to building lasting partnerships and ensuring continuous improvement."
    },
    { 
      icon: Users2, 
      title: "Loyalty", 
      description: "Building lasting relationships with our clients and partners",
      details: "Our success is measured by the long-term relationships we build. We stand by our clients through challenges and victories, providing unwavering support and dedication to their growth."
    }
  ];

  const expertise = [
    "Network Design & Implementation",
    "High-Speed Wireless Communications",
    "LAN & WAN Environments",
    "Application Training",
    "Technology Consulting",
    "Network Training"
  ];

  return (
    <>
      <SEOHead 
        title="About UCX - Texas' Leading Managed IT Services Provider | Company Overview"
        description="Learn about UCX, Texas' premier managed IT services provider. Founded in Dallas, we serve businesses across Texas with enterprise-grade IT solutions, 24/7 support, and expert technology consulting since 2010."
        keywords="about UCX, Texas IT company, Dallas managed IT provider, UCX history, IT services company Texas, managed service provider background, enterprise IT solutions Texas"
        canonical="https://ucx.co/about"
      />
      <div className="pt-16">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80')] opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Empowering Success Through Technology
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Unified ConneXions, Inc. is a Dallas, Texas-based company, committed to providing expert, professional, and state-of-the-art IT services to corporate, government, and educational institutions.
            </p>
          </div>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-sm text-blue-600 font-semibold uppercase tracking-wider">
                Our Mission
              </span>
            </div>
          </div>
          
          <blockquote className="mt-8 text-center">
            <p className="text-2xl font-medium text-gray-900 max-w-3xl mx-auto">
              "Unified ConneXions is dedicated to helping corporate, government, and educational institutions by delivering world class technology solutions and services while incorporating the values of passion, integrity, commitment, and loyalty."
            </p>
          </blockquote>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
            <p className="mt-4 text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="relative group h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10"></div>
                <div className="relative bg-white rounded-xl shadow-lg p-8 h-full transform group-hover:-translate-y-2 group-hover:scale-[1.02] transition-all duration-300 border border-gray-100 group-hover:border-transparent">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-bl-[100%] opacity-30 group-hover:opacity-0 transition-opacity -rotate-6"></div>
                  
                  <div className="relative z-10">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-white transition-colors duration-300">
                      <value.icon className="h-8 w-8 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-black transition-colors duration-300">
                      {value.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 group-hover:text-black transition-colors duration-300">
                      {value.description}
                    </p>
                    
                    <p className="text-sm text-gray-500 group-hover:text-black/80 transition-colors duration-300">
                      {value.details}
                    </p>
                  </div>
                  
                  <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-blue-50 to-transparent rounded-tl-[100%] opacity-30 group-hover:opacity-0 transition-opacity rotate-6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expertise Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Expertise</h2>
              <p className="text-xl text-gray-600 mb-8">
                We offer a wide range of services for any size organization, delivering quality, turnkey solutions that meet all of your IT requirements through cost-effective solutions.
              </p>
              <ul className="space-y-4">
                {expertise.map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700 group">
                    <CheckCircle className="h-5 w-5 text-blue-600 mr-3" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg blur-xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <img 
                src="https://images.unsplash.com/photo-1504384764586-bb4cdc1707b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" 
                alt="IT Professionals"
                className="relative rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 rounded-2xl shadow-xl overflow-hidden">
            <div className="relative px-8 py-16 md:px-16">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80')] opacity-10"></div>
              <div className="relative max-w-3xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Ready to Transform Your IT Infrastructure?
                </h2>
                <p className="text-xl text-blue-100 mb-8">
                  Let's discuss how we can help move your organization forward with the right technology solutions.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors group"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}